<template>
  <v-app-bar :absolute="true" elevation="4" data-testid="nav-bar">
    <v-app-bar-title>
      <div class="d-flex">
        <router-link to="/">
          <h1>
            <img
              v-if="showSmallerLogo"
              class="d-block"
              :src="getNovaLogoMarkSrc"
              alt="Nova logo"
              width="24"
            />
            <img
              v-else
              class="d-block"
              :src="getNovaLogoSrc"
              alt="Nova logo"
              width="110"
            />
          </h1>
        </router-link>
      </div>
    </v-app-bar-title>

    <template
      v-for="(link, navIndex) in visibleLinks"
      :key="`navlink-${navIndex}`"
    >
      <SoButton
        :id="`navlink-${navIndex}`"
        :title="link.title"
        :icon="link.menu ? 'chevron-down' : undefined"
        :[link.attr]="link.route"
        variant="flat"
        data-testid="nav-bar-link-button"
        type="navbar-link"
        :ripple="!link.menu"
        class="nav-bar-link-button"
      />

      <v-menu
        v-if="link.menu && link.menu.length"
        :activator="`#navlink-${navIndex}`"
        offset="15"
        open-delay="100"
        location="bottom"
        open-on-hover
      >
        <v-list>
          <v-list-item
            v-for="menuItem in link.menu"
            :key="menuItem.title"
            :title="menuItem.title"
            :[menuItem.attr]="menuItem.route"
            :target="menuItem.target"
            :append-icon="menuItem.target === '_blank' ? iconOpenNewTab : ''"
          >
          </v-list-item>
        </v-list>
      </v-menu>
    </template>

    <template v-if="visibleSearch">
      <template v-if="showCollapsibleSearchbar">
        <SoButton id="navlink-search" type="navbar-icon" icon="search" />
        <v-menu
          activator="#navlink-search"
          offset="15"
          location="bottom"
          :close-on-content-click="false"
        >
          <v-list>
            <v-list-item>
              <CustomerSearchWidget class="pa-3" />
            </v-list-item>
          </v-list>
        </v-menu>
      </template>

      <CustomerSearchWidget v-else class="ml-2 pt-1" />
    </template>

    <v-spacer v-else />

    <SoButton type="navbar-icon" icon="cog" to="/settings" class="mr-4" />
  </v-app-bar>
</template>

<script lang="ts">
  import NovaLogoBlack from "@/assets/Nova_Logo_Horizontal.svg"
  import NovaLogoWhite from "@/assets/Nova_Logo_Horizontal_white.svg"
  import NovaLogoMarkBlack from "@/assets/Nova_Logo_LogoMark.svg"
  import NovaLogoMarkWhite from "@/assets/Nova_Logo_LogoMark-White.svg"
  import SoButton from "@/components/formControls/SoButton.vue"
  import { externalRoutes } from "@/constants/routes/externalRoutes"
  import { useThemeHelper } from "@/theme/themeHelper"
  import IPermission from "@/types/IPermissions"
  import CustomerSearchWidget from "@/views/customer/search/widgets/CustomerSearchWidget.vue"
  import { defineComponent, inject } from "vue"

  type Link = {
    title: string
    route?: string
    menu?: Link[]
    target?: string
    attr: string
  }

  export default defineComponent({
    components: {
      SoButton,
      CustomerSearchWidget,
    },
    // eslint-disable-next-line vue/component-api-style
    setup() {
      const permission = inject<IPermission>("$permission")
      return {
        permission,
      }
    },
    data() {
      return {
        theme: useThemeHelper(),
        iconOpenNewTab: "external-link sm",
      }
    },
    computed: {
      showSmallerLogo() {
        const maxWidth = 1355
        return this.$vuetify.display.width <= maxWidth
      },
      showCollapsibleSearchbar() {
        const maxWidth = 1205
        return this.$vuetify.display.width <= maxWidth
      },
      getNovaLogoSrc() {
        return this.theme?.name === "novaLightTheme"
          ? NovaLogoBlack
          : NovaLogoWhite
      },
      getNovaLogoMarkSrc() {
        return this.theme?.name === "novaLightTheme"
          ? NovaLogoMarkBlack
          : NovaLogoMarkWhite
      },
      visibleLinks() {
        return this.filterLinksByPermission(this.links)
      },
      visibleSearch() {
        return this.permission?.canAccessCustomerSearch()
      },
      links(): Link[] {
        return [
          {
            title: "Admin",
            attr: "",
            menu: [
              {
                title: "Users",
                attr: "to",
                route: "/admin/users",
              },
              {
                title: "Synchronized Accounts",
                attr: "to",
                route: "/admin/sync-accounts",
              },
              {
                title: "Test Accounts",
                attr: "to",
                route: "/admin/test-accounts",
              },
              {
                title: "Energy Products",
                attr: "to",
                route: "/admin/energy-products",
              },
              {
                title: "Features Admin",
                attr: "to",
                route: "/admin/features-admin",
              },
              {
                title: "Configurations Admin",
                attr: "to",
                route: "/admin/configurations-admin",
              },
              {
                title: "Maintenance Banner",
                attr: "to",
                route: "/admin/maintenance",
              },
              {
                title: "Newsfeed",
                attr: "to",
                route: "/admin/newsfeed",
              },
              {
                title: "Email Templates",
                attr: "to",
                route: "/admin/email-templates",
              },
              {
                title: "Transactional Email Volume",
                attr: "to",
                route: "/admin/email-volumes",
              },
              {
                title: "Email Throttles",
                attr: "to",
                route: "/admin/email-throttles",
              },
              {
                title: "Marketing banners",
                attr: "to",
                route: "/admin/marketing-banners",
              },
            ],
          },
          {
            title: "Operations",
            attr: "",
            menu: [
              {
                title: "Vouchers",
                attr: "to",
                route: "/operations/vouchers",
              },
              {
                title: "DD Reviews",
                attr: "to",
                route: "/operations/direct-debit-reviews",
              },
              {
                title: "DCA management",
                attr: "to",
                route: "/operations/dca-management",
              },
              {
                title: "Readings",
                attr: "to",
                route: "/operations/meter-readings",
              },
              {
                title: "SMS Sender",
                attr: "to",
                route: "/operations/sms-sender",
              },
              {
                title: "Onboarding",
                attr: "to",
                route: "/operations/onboarding",
              },
              {
                title: "Smart Meters",
                attr: "to",
                route: "/operations/smart-meters",
              },
              {
                title: "Trades",
                attr: "to",
                route: "/operations/trades",
              },
              {
                title: "Complaints (Beta)",
                attr: "to",
                route: "/operations/complaints",
              },
              {
                title: "Deposit Requests",
                attr: "to",
                route: "/operations/deposit-requests",
              },
              {
                title: "Freshdesk Tickets",
                attr: "to",
                route: "/operations/freshdesk-contacts",
              },
              {
                title: "Prize Draws",
                attr: "to",
                route: "/operations/loyalty/draws",
              },
              {
                title: "Services",
                attr: "to",
                route: "/admin/services",
              },
              {
                title: externalRoutes.WHDApplications.name,
                attr: "href",
                target: "_blank",
                route: externalRoutes.WHDApplications.path,
              },
              {
                title: "EV Smart Charging",
                attr: "to",
                route: "/operations/smart-charging",
              },
            ],
          },
          {
            title: "Customer care",
            attr: "",
            menu: [
              {
                title: externalRoutes.AWSConnect.name,
                attr: "href",
                target: "_blank",
                route: externalRoutes.AWSConnect.path,
              },
              {
                title: externalRoutes.Teleopti.name,
                attr: "href",
                target: "_blank",
                route: externalRoutes.Teleopti.path,
              },
            ],
          },
          {
            title: "Knowledge",
            attr: "",
            menu: [
              {
                title: externalRoutes.KnowledgeBase.name,
                attr: "href",
                target: "_blank",
                route: externalRoutes.KnowledgeBase.path,
              },
              {
                title: externalRoutes.PhoneQueues.name,
                attr: "href",
                target: "_blank",
                route: externalRoutes.PhoneQueues.path,
              },
              {
                title: "Tariff Quoter",
                attr: "to",
                route: "/tariff-quoter",
              },
              {
                title: externalRoutes.ECOES.name,
                attr: "href",
                target: "_blank",
                route: externalRoutes.ECOES.path,
              },
              {
                title: externalRoutes.XOServe.name,
                attr: "href",
                target: "_blank",
                route: externalRoutes.XOServe.path,
              },
              {
                title: externalRoutes.SoGoodToKnowPage.name,
                attr: "href",
                target: "_blank",
                route: externalRoutes.SoGoodToKnowPage.path,
              },
            ],
          },
          {
            title: "Forms",
            attr: "",
            menu: [
              {
                title: externalRoutes.ThirdPartyInvestigations.name,
                attr: "href",
                target: "_blank",
                route: externalRoutes.ThirdPartyInvestigations.path,
              },
              {
                title: "Open bugs",
                attr: "to",
                route: "/issues",
              },
              {
                title: externalRoutes.DataReqIncident.name,
                attr: "href",
                target: "_blank",
                route: externalRoutes.DataReqIncident.path,
              },
              {
                title: externalRoutes.DataReqOther.name,
                attr: "href",
                target: "_blank",
                route: externalRoutes.DataReqOther.path,
              },
            ],
          },
        ]
      },
    },
    methods: {
      filterLinksByPermission(links: Link[]) {
        const permittedLinks: Link[] = []
        for (const link of links) {
          const linkCpy: Link = { ...link }

          if (linkCpy.menu?.length) {
            linkCpy.menu = this.filterLinksByPermission(linkCpy.menu)
          }

          if (
            (!linkCpy.route ||
              this.permission?.canAccessRoute(linkCpy.route)) &&
            linkCpy.menu?.length !== 0
          ) {
            permittedLinks.push(linkCpy)
          }
        }
        return permittedLinks
      },
    },
  })
</script>
<style scoped>
  :deep(.v-list-item-title) {
    font-weight: 500 !important;
  }
  :deep(.v-list-item__prepend > .v-icon) {
    opacity: 1 !important;
  }
  :deep(.v-btn__content) {
    display: inline-block;
  }
  :deep(
    .nav-bar-link-button[aria-expanded="true"] .v-btn .v-btn__content::after
  ),
  :deep(.nav-bar-link-button .v-btn .v-btn__content::after) {
    content: "";
    display: block;
    width: 0;
    height: 2px;
    background: rgb(var(--v-theme-on-surface));
    transition: width 0.2s;
  }
  :deep(.nav-bar-link-button[aria-expanded="true"] .v-btn) {
    opacity: 1 !important;
  }

  @media (max-width: 800px) {
    :deep(.nav-bar-link-button .v-btn) {
      padding: 0 12px;
    }
    :deep(.nav-bar-link-button .v-btn__content > .v-icon--end) {
      margin-left: 0px;
    }
  }

  :deep(
    .nav-bar-link-button[aria-expanded="true"] .v-btn .v-btn__content:after
  ),
  :deep(.nav-bar-link-button .v-btn:hover .v-btn__content:after) {
    width: 100%;
  }
</style>
